import { ExternalLookupType } from '@/typings';
import type { DropdownInputMode } from '@/ui-kit/dropdown/dropdown';
import { FACILITY_TYPEAHEAD_HINT, isFacilityLookup } from './facilityFreeText';
import { PO_BOX_NOT_ACCEPTED_MESSAGE } from './poBox';
import type { TypeaheadHintState } from './resolveTypeaheadHintState';

/** Lookup types whose typeahead is an address street. */
export const ADDRESS_LOOKUP_TYPES: ReadonlySet<ExternalLookupType> = new Set([
  ExternalLookupType.Address,
  ExternalLookupType.FacilityAddress,
]);

/** Persistent default + revert-warning hint copy for a single-value typeahead field. */
export const getTypeaheadHintText = (
  lookupType: ExternalLookupType | undefined,
  allowPoBox = false,
): { defaultHint: string; revertWarning: string } => {
  const isAddress = lookupType !== undefined && ADDRESS_LOOKUP_TYPES.has(lookupType);
  // A facility keeps typed text instead of reverting it, so only its default hint differs.
  const otherDefaultHint = isFacilityLookup({ lookupType })
    ? FACILITY_TYPEAHEAD_HINT
    : 'Start typing and select an option from the list.';

  return isAddress
    ? {
        defaultHint: allowPoBox
          ? 'Start typing and select an address from the list, or enter a PO Box.'
          : 'Start typing and select an address from the list.',
        revertWarning: 'Select an address from the suggestions to update it.',
      }
    : {
        defaultHint: otherDefaultHint,
        revertWarning: 'Select an option from the suggestions to update it.',
      };
};

/** An address street's hint for a PO box input mode; null in search mode. */
export const getPoBoxModeHint = (
  mode: DropdownInputMode,
  allowPoBox: boolean,
): TypeaheadHintState | null => {
  if (mode === 'free') {
    return {
      state: 'default',
      stateText: 'PO Box — enter the city, state and ZIP below.',
    };
  }
  if (mode === 'blocked') {
    return {
      state: 'warning',
      stateText: allowPoBox
        ? 'Add the box number, e.g. PO Box 123.'
        : PO_BOX_NOT_ACCEPTED_MESSAGE,
    };
  }
  return null;
};
